import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Avatar,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import KeyIcon from '@mui/icons-material/Key';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { CardMembershipTwoTone } from '@mui/icons-material';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const { forgotPassword } = useAuth();
  const { tenant } = useTenant();
  
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Email is required');
      setSnackbarOpen(true);
      return;
    }
    
    setLoading(true);
    setError('');
    setMessage('');

    try {
      // Store the email in localStorage for the reset confirmation
      localStorage.setItem('resetEmail', email);
      
      await forgotPassword(email);
      setMessage('If your email is registered, you will receive password reset instructions.');
      setIsSubmitted(true);
    } catch (error) {
      console.error('Forgot password error:', error);
      // For security reasons, we don't want to reveal if an email exists or not
      setMessage('If your email is registered, you will receive password reset instructions.');
      setIsSubmitted(true);
    } finally {
      setLoading(false);
      setSnackbarOpen(true);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: '#f5f7fa',
        py: 8,
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to="/login"
          sx={{
            color: '#2e3f6f',
            mb: 4,
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'rgba(46, 63, 111, 0.08)'
            }
          }}
        >
          Back to Login
        </Button>

        <Paper
          elevation={10}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            overflow: 'hidden',
            borderRadius: 4,
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Left Side - Branding */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 6,
              flexBasis: '40%',
              backgroundColor: '#2e3f6f',
              color: 'white',
              textAlign: 'center',
              position: 'relative',
              backgroundImage: 'linear-gradient(135deg, rgba(46, 63, 111, 0.95) 0%, rgba(46, 63, 111, 0.8) 100%)',
              backgroundSize: 'cover',
              borderRadius: '4px 0 0 4px',
            }}
          >
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: -1,
              opacity: 0.1,
              background: 'url(/abstract-pattern.svg) center/cover no-repeat',
            }} />
            {tenant?.logo ? (
              <Box
                component="img"
                src={tenant.logo}
                alt={tenant.name || 'Company Logo'}
                sx={{
                  width: 100,
                  height: 100,
                  mb: 4,
                  objectFit: 'contain',
                }}
              />
            ) : (
              <Avatar
                sx={{
                  bgcolor: 'white',
                  color: '#2e3f6f',
                  width: 84,
                  height: 84,
                  mb: 4,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                  fontSize: 40,
                }}
              >
                {tenant?.name?.[0] || <CardMembershipTwoTone sx={{ fontSize: 40 }} />}
              </Avatar>
            )}
            <Typography variant="h4" component="h1" fontWeight="700" gutterBottom>
              {tenant?.name || 'Transform Portal'}
            </Typography>
            <Typography variant="body1" sx={{ mt: 2, maxWidth: 280 }}>
              Reset your password to regain access to your account and continue your learning journey.
            </Typography>
          </Box>

          {/* Right Side - Form */}
          <Box
            sx={{
              p: { xs: 4, sm: 6 },
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {!isSubmitted ? (
              <>
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                  <Avatar
                    sx={{
                      mx: 'auto',
                      bgcolor: '#2e3f6f',
                      width: 56,
                      height: 56,
                      mb: 2,
                      boxShadow: '0 4px 12px rgba(46, 63, 111, 0.2)',
                    }}
                  >
                    <KeyIcon />
                  </Avatar>
                  <Typography variant="h5" component="h2" fontWeight="700" color="text.primary">
                    Forgot Password?
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    Enter your email address below, and we'll send you instructions to reset your password.
                  </Typography>
                </Box>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    label="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    margin="normal"
                    required
                    autoComplete="email"
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <EmailIcon sx={{ color: 'text.secondary', mr: 1 }} />
                      ),
                      sx: { borderRadius: 2 }
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: '#2e3f6f',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#2e3f6f',
                      },
                    }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      bgcolor: '#2e3f6f',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1rem',
                      borderRadius: 2,
                      boxShadow: '0 5px 15px rgba(46, 63, 111, 0.2)',
                      '&:hover': {
                        bgcolor: '#243252',
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Send Reset Link'
                    )}
                  </Button>
                </Box>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Avatar
                  sx={{
                    mx: 'auto',
                    bgcolor: '#4caf50',
                    width: 70,
                    height: 70,
                    mb: 3,
                    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.2)',
                  }}
                >
                  <EmailIcon sx={{ fontSize: 36 }} />
                </Avatar>
                <Typography variant="h5" component="h2" fontWeight="700" color="text.primary" gutterBottom>
                  Check Your Email
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  If the email address {email} is associated with an account, you'll receive password reset instructions shortly.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                  Don't receive an email? Check your spam folder or try again.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsSubmitted(false);
                    setEmail('');
                  }}
                  sx={{
                    mr: 2,
                    borderColor: '#2e3f6f',
                    color: '#2e3f6f',
                    fontWeight: 500,
                    textTransform: 'none',
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: '#1a2a5e',
                      bgcolor: 'rgba(46, 63, 111, 0.04)',
                    },
                  }}
                >
                  Try Again
                </Button>
                <Button
                  variant="contained"
                  component={RouterLink}
                  to="/login"
                  sx={{
                    bgcolor: '#2e3f6f',
                    fontWeight: 500,
                    textTransform: 'none',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(46, 63, 111, 0.2)',
                    '&:hover': {
                      bgcolor: '#243252',
                    },
                  }}
                >
                  Back to Login
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      </Container>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={error ? 'error' : 'success'}
          variant="filled"
          elevation={6}
          sx={{ width: '100%' }}
        >
          {error || message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ForgotPasswordPage;
